import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberBodyAttract.module.scss";
import { BODY_ATTRACT } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Hero images */
import mrImg from "@/assets/UI/memberCreate/q7Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q7Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q7MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q7MrsM.webp";

/* Floating background animation */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta */
const ATTRACT_META = {
  fit_toned: "💪",
  curvy_soft: "🍑",
  athletic_strong: "🔥",
  natural_gentle: "🌸",
  unique_variety: "💫",
  open_all: "💎",
};

export default function CreateMemberBodyAttract() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const { memberType, isCouple: isCoupleCtx } = useMemberLayoutContext() || {};

  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoices, setMemberChoices] = useState([]);
  const [partnerChoices, setPartnerChoices] = useState([]);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.body_attract.title", {
    defaultValue: "Body types you’re attracted to",
  });

  const subtitle = !isCouple
    ? t("onboarding.body_attract.single.question", {
        defaultValue: "What kind of body energy pulls you in?",
      })
    : t("onboarding.body_attract.couple.lead", {
        defaultValue: "Choose for yourself, then for your partner.",
      });

  const options = BODY_ATTRACT.map((value) => ({
    value,
    emoji: ATTRACT_META[value] || "✨",
    title: t(`onboarding.body_attract.options.${value}.label`, {
      defaultValue: value,
    }),
    hint: t(`onboarding.body_attract.options.${value}.hint`, {
      defaultValue: "Tap to add this attraction",
    }),
  }));

  function toggle(list, value) {
    return list.includes(value)
      ? list.filter((v) => v !== value)
      : [...list, value];
  }

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info("Pick at least one that attracts you");
      return;
    }

    if (isCouple && !partnerChoices.length) {
      toast.info("Your partner needs to choose too");
      return;
    }

    setSubmitting(true);
    try {
      const payload = isCouple
        ? {
            memberBodyAttract: memberChoices,
            partnerBodyAttract: partnerChoices,
          }
        : { memberBodyAttract: memberChoices };

      const { data } = await api.post(
        "/v1/member-create/body_attract",
        payload,
        { skipToast: true },
      );

      await refreshMe();
      navigate(data?.next || "/member/sexual_environment", { replace: true });
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Background */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            variants={floatSlow}
            initial="hidden"
            animate="show"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            variants={floatSlow}
            initial="hidden"
            animate="show"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{subtitle}</div>
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit}>
            <fieldset className={cls.options}>
              {options.map((opt) => (
                <label
                  key={opt.value}
                  className={`${cls.option} ${
                    memberChoices.includes(opt.value) ? cls.optionActive : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={memberChoices.includes(opt.value)}
                    onChange={() =>
                      setMemberChoices((p) => toggle(p, opt.value))
                    }
                  />

                  <span className={cls.optionBody}>
                    <span className={cls.optionEmoji}>{opt.emoji}</span>
                    <span className={cls.optionText}>
                      <strong>{opt.title}</strong>
                      <small>{opt.hint}</small>
                    </span>
                    <span className={cls.optionDot} />
                  </span>
                </label>
              ))}
            </fieldset>

            {isCouple && (
              <div className={cls.partnerBlock}>
                <div className={cls.partnerTitle}>
                  {t("onboarding.body_attract.partner.turn")}
                </div>

                <fieldset className={cls.options}>
                  {options.map((opt) => (
                    <label
                      key={`p-${opt.value}`}
                      className={`${cls.option} ${
                        partnerChoices.includes(opt.value)
                          ? cls.optionActive
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={partnerChoices.includes(opt.value)}
                        onChange={() =>
                          setPartnerChoices((p) => toggle(p, opt.value))
                        }
                      />

                      <span className={cls.optionBody}>
                        <span className={cls.optionEmoji}>{opt.emoji}</span>
                        <span className={cls.optionText}>
                          <strong>{opt.title}</strong>
                          <small>{opt.hint}</small>
                        </span>
                        <span className={cls.optionDot} />
                      </span>
                    </label>
                  ))}
                </fieldset>
              </div>
            )}

            <div className={cls.actions}>
              <button
                className={cls.submit}
                type="submit"
                disabled={
                  submitting ||
                  !memberChoices.length ||
                  (isCouple && !partnerChoices.length)
                }
              >
                {submitting ? "Saving…" : "Continue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
